<script setup lang="ts">
/**
 * „Später erinnern“ of one notification (I-2915): the kit's menu button with
 * the instants it can be put off to (`notificationSnooze.ts`). Emits the
 * chosen instant; the caller snoozes. Used on the rows of the bell's panel
 * and of the notifications page.
 */
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import AlarmClock from '@lucide/vue/dist/esm/icons/alarm-clock.mjs';
import {
  NxContextMenuItem,
  NxContextMenuLabel,
  NxMenuButton,
} from "@blpde/nx-ui";
import {
  notificationSnoozePresets,
  type NotificationSnoozePreset,
} from "@/notificationSnooze";

defineProps<{
  disabled?: boolean;
}>();

const emit = defineEmits<{
  /** An instant was chosen. */
  snooze: [until: Date];
}>();

const { t } = useI18n();
const open = ref(false);
// As of the moment the menu opens: „this evening“ goes once it has passed.
const presets = ref<NotificationSnoozePreset[]>(notificationSnoozePresets());
watch(open, (isOpen) => {
  if (isOpen) presets.value = notificationSnoozePresets();
});

const label = computed(() => t("connect.notifications.snooze"));

</script>

<template>
  <NxMenuButton
    v-model:open="open"
    :label="label"
    :disabled="disabled"
    data-testid="notification-snooze"
  >
    <template #icon>
      <AlarmClock class="nxt-icon-sm" aria-hidden="true" />
    </template>
    <NxContextMenuLabel>{{ label }}</NxContextMenuLabel>
    <NxContextMenuItem
      v-for="preset in presets"
      :key="preset.id"
      :data-testid="`notification-snooze-${preset.id}`"
      @select="emit('snooze', preset.until)"
    >
      {{ t(`connect.notifications.snoozePresets.${preset.id}`) }}
    </NxContextMenuItem>
  </NxMenuButton>
</template>
